// EduBridge Network — shared hook that reads company contact info + social
// links from the public site-settings endpoint. Consumed by the public header,
// footer, contact page and any other user-facing surface that shows company
// details. Client Admin edits these under Core Settings → Public Site.
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = {
  company_name: 'Edu Bridge Network',
  primary_address: '',
  secondary_address: '',
  phones: [],
  emails: [],
  logo_url: '',
  logo_file_id: '',
  social: { facebook: '', instagram: '', linkedin: '', youtube: '', twitter: '', tiktok: '' },
};

// Module-level cache so multiple components hitting this hook fire a single request
let _cached = null;
let _inflight = null;
const _listeners = new Set();

const fetchOnce = async () => {
  if (_cached) return _cached;
  if (_inflight) return _inflight;
  _inflight = axios
    .get(`${API}/public/site-settings`)
    .then((res) => {
      _cached = { ...DEFAULTS, ...(res.data || {}), social: { ...DEFAULTS.social, ...(res.data?.social || {}) } };
      _listeners.forEach((fn) => fn(_cached));
      return _cached;
    })
    .catch(() => {
      _cached = DEFAULTS;
      return _cached;
    })
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
};

// Called by the admin form after a successful save so every consumer refreshes
export const refreshSiteSettings = async () => {
  _cached = null;
  return fetchOnce();
};

export const useSiteSettings = () => {
  const [settings, setSettings] = useState(_cached || DEFAULTS);

  useEffect(() => {
    let mounted = true;
    const listener = (data) => {
      if (mounted) setSettings(data);
    };
    _listeners.add(listener);
    fetchOnce().then((data) => mounted && setSettings(data));
    return () => {
      mounted = false;
      _listeners.delete(listener);
    };
  }, []);

  return settings;
};

export default useSiteSettings;
